import { useState, useEffect } from 'react';
import { tickerApi } from '../services/tickerApi';
import { GetLatestAnalystViewsRequest, TickerAnalystViewsData, AnalystViewDisplayData } from '../types/analystViews';

// Helper function to safely format currency
const safeFormatCurrency = (value: number | undefined, decimals: number = 2): string => {
  if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
    return '-';
  }
  return `$${value.toFixed(decimals)}`;
};

// Shared transformation function for analyst views data
export const transformAnalystViewsData = (rawData: TickerAnalystViewsData[]): AnalystViewDisplayData[] => {
  return rawData.map(tickerData => {
    const displayData: AnalystViewDisplayData = {
      tickerSymbol: tickerData.tickerSymbol,
    };

    // Process analyst views by category
    const viewsByCategory = tickerData.analystViews.reduce((acc, view) => {
      if (!acc[view.category]) {
        acc[view.category] = {};
      }
      // Only include valid numeric values, skip NaN or invalid values
      if (view.value !== null && !isNaN(view.value) && isFinite(view.value)) {
        acc[view.category][view.key] = view.value;
      }
      return acc;
    }, {} as Record<string, Record<string, number>>);

    // Price Targets
    const priceTargets = viewsByCategory.price_targets || {};
    if (priceTargets.targetMeanPrice) {
      displayData.averagePriceTarget = priceTargets.targetMeanPrice;
    }
    if (priceTargets.targetHighPrice && priceTargets.targetLowPrice) {
      const lowPrice = safeFormatCurrency(priceTargets.targetLowPrice);
      const highPrice = safeFormatCurrency(priceTargets.targetHighPrice);
      displayData.priceTargetRange = `${lowPrice} - ${highPrice}`;
    }

    // Recommendations
    const recommendations = viewsByCategory.recommendations || {};
    const totalRecommendations = (recommendations.strongBuy || 0) + (recommendations.buy || 0) + 
                               (recommendations.hold || 0) + (recommendations.sell || 0) + 
                               (recommendations.strongSell || 0);
    
    if (totalRecommendations > 0) {
      displayData.strongBuyCount = recommendations.strongBuy || 0;
      displayData.buyCount = recommendations.buy || 0;
      displayData.holdCount = recommendations.hold || 0;
      displayData.sellCount = recommendations.sell || 0;
      displayData.strongSellCount = recommendations.strongSell || 0;
      
      displayData.strongBuyPercent = ((recommendations.strongBuy || 0) / totalRecommendations) * 100;
      displayData.buyPercent = ((recommendations.buy || 0) / totalRecommendations) * 100;
      displayData.holdPercent = ((recommendations.hold || 0) / totalRecommendations) * 100;
      displayData.sellPercent = ((recommendations.sell || 0) / totalRecommendations) * 100;
      displayData.strongSellPercent = ((recommendations.strongSell || 0) / totalRecommendations) * 100;
      
      // Calculate average recommendation (simplified)
      const score = (recommendations.strongBuy || 0) * 5 + (recommendations.buy || 0) * 4 + 
                   (recommendations.hold || 0) * 3 + (recommendations.sell || 0) * 2 + 
                   (recommendations.strongSell || 0) * 1;
      const avgScore = score / totalRecommendations;
      
      let recommendationText = '';
      if (avgScore >= 4.5) recommendationText = 'Strong Buy';
      else if (avgScore >= 3.5) recommendationText = 'Buy';
      else if (avgScore >= 2.5) recommendationText = 'Hold';
      else if (avgScore >= 1.5) recommendationText = 'Sell';
      else recommendationText = 'Strong Sell';
      
      displayData.averageRecommendation = `${recommendationText} (${avgScore.toFixed(1)})`;
    }

    // Earnings
    const earnings = viewsByCategory.earnings || {};
    
    // This Quarter (0q)
    if (earnings['0q_eps_avg']) {
      displayData.thisQuarterEpsAvg = earnings['0q_eps_avg'];
      if (earnings['0q_eps_high'] && earnings['0q_eps_low']) {
        const lowEps = safeFormatCurrency(earnings['0q_eps_low']);
        const highEps = safeFormatCurrency(earnings['0q_eps_high']);
        displayData.thisQuarterEpsRange = `${lowEps} - ${highEps}`;
      }
    }
    if (earnings['0q_eps_yearAgoEps']) {
      displayData.thisQuarterEpsYearAgo = earnings['0q_eps_yearAgoEps'];
    }
    
    // Next Quarter (+1q)
    if (earnings['+1q_eps_avg']) {
      displayData.nextQuarterEpsAvg = earnings['+1q_eps_avg'];
      if (earnings['+1q_eps_high'] && earnings['+1q_eps_low']) {
        const lowEps = safeFormatCurrency(earnings['+1q_eps_low']);
        const highEps = safeFormatCurrency(earnings['+1q_eps_high']);
        displayData.nextQuarterEpsRange = `${lowEps} - ${highEps}`;
      }
    }
    if (earnings['+1q_eps_yearAgoEps']) {
      displayData.nextQuarterEpsYearAgo = earnings['+1q_eps_yearAgoEps'];
    }
    
    // This Year (0y)
    if (earnings['0y_eps_avg']) {
      displayData.thisYearEpsAvg = earnings['0y_eps_avg'];
      if (earnings['0y_eps_high'] && earnings['0y_eps_low']) {
        const lowEps = safeFormatCurrency(earnings['0y_eps_low']);
        const highEps = safeFormatCurrency(earnings['0y_eps_high']);
        displayData.thisYearEpsRange = `${lowEps} - ${highEps}`;
      }
    }
    if (earnings['0y_eps_yearAgoEps']) {
      displayData.thisYearEpsYearAgo = earnings['0y_eps_yearAgoEps'];
    }
    
    // Next Year (+1y)
    if (earnings['+1y_eps_avg']) {
      displayData.nextYearEpsAvg = earnings['+1y_eps_avg'];
      if (earnings['+1y_eps_high'] && earnings['+1y_eps_low']) {
        const lowEps = safeFormatCurrency(earnings['+1y_eps_low']);
        const highEps = safeFormatCurrency(earnings['+1y_eps_high']);
        displayData.nextYearEpsRange = `${lowEps} - ${highEps}`;
      }
    }
    if (earnings['+1y_eps_yearAgoEps']) {
      displayData.nextYearEpsYearAgo = earnings['+1y_eps_yearAgoEps'];
    }

    return displayData;
  });
};

interface UseAnalystViewsResult {
  data: AnalystViewDisplayData[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => Promise<void>;
}

export const useAnalystViews = (tickerList: string[]): UseAnalystViewsResult => {
  const [data, setData] = useState<AnalystViewDisplayData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);


  const fetchAnalystViews = async () => {
    if (tickerList.length === 0) {
      setData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: GetLatestAnalystViewsRequest = {
        ticker_symbols: tickerList,
        category: 'all',
        key: 'all'
      };

      const response = await tickerApi.getLatestAnalystViews(request);
      
      if (response.success) {
        const transformedData = transformAnalystViewsData(response.data);
        setData(transformedData);
        setLastUpdated(new Date());
      } else {
        setError(response.message || 'Failed to fetch analyst views');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching analyst views:', err);
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchAnalystViews();
  };

  useEffect(() => {
    fetchAnalystViews();
  }, [tickerList.join(',')]); // Re-fetch when ticker list changes

  return {
    data,
    loading,
    error,
    lastUpdated,
    refetch,
  };
};
